/**
 * Parses an email message body to properly display its content
 * Handles multipart MIME messages, HTML content, and plain text
 * 
 * @param {string} messageBody - The raw email message body
 * @returns {Object} - { content: string, isHtml: boolean }
 */
export const parseEmailContent = (messageBody) => {
  if (!messageBody) {
    return { 
      content: null, 
      isHtml: false 
    };
  }

  // Helper function to decode quoted-printable encoding
  const decodeQuotedPrintable = (text) => {
    if (!text) return text;
    
    // First handle common Nordic character sequences that appear in UTF-8 + quoted-printable
    const withNordicChars = text
      // Swedish/Norwegian/Danish characters
      .replace(/=C3=A5/g, 'å')        // å
      .replace(/=C3=A4/g, 'ä')        // ä
      .replace(/=C3=B6/g, 'ö')        // ö
      .replace(/=C3=85/g, 'Å')        // Å
      .replace(/=C3=84/g, 'Ä')        // Ä
      .replace(/=C3=96/g, 'Ö')        // Ö
      .replace(/=C3=B8/g, 'ø')        // ø (Danish/Norwegian)
      .replace(/=C3=98/g, 'Ø')        // Ø (Danish/Norwegian)
      .replace(/=C3=A6/g, 'æ')        // æ (Danish/Norwegian)
      .replace(/=C3=86/g, 'Æ')        // Æ (Danish/Norwegian)
      .replace(/=C3=A9/g, 'é')        // é
      .replace(/=C3=89/g, 'É')        // É
      // We'll handle these with the fixDoubleEncodedText function later
      // Common symbols in Nordic emails
      .replace(/=E2=80=93/g, '-')     // en dash
      .replace(/=E2=80=94/g, '-')     // em dash
      .replace(/=E2=80=99/g, "'")     // right single quotation mark
      .replace(/=E2=80=98/g, "'")     // left single quotation mark
      .replace(/=E2=80=9C/g, '"')     // left double quotation mark
      .replace(/=E2=80=9D/g, '"')     // right double quotation mark
      .replace(/=E2=80=A6/g, '...')   // horizontal ellipsis
      .replace(/=E2=80=AF/g, ' ')     // narrow no-break space
      // Additional problematic character in Nordic text
      .replace(/=C2=A0/g, ' ')        // non-breaking space
      .replace(/=20/g, ' ');          // space
      
    // Then handle the remaining quoted-printable encoding generally
    return withNordicChars
      // Replace any remaining hex sequences
      .replace(/=([0-9A-F]{2})/g, (match, hex) => {
        try {
          return String.fromCharCode(parseInt(hex, 16));
        } catch (e) {
          return match; // Return original if parsing fails
        }
      })
      // Fix line continuations (quoted-printable soft line breaks)
      .replace(/=\r?\n/g, '')
      // Handle HTML entities
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  // Remove MIME boundaries and headers completely
  const removeMIMEHeadersAndBoundaries = (text) => {
    if (!text) return text;

    // Remove boundary markers and content headers
    return text
      .replace(/--[0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*(?:--)?\r?\n/g, '')
      .replace(/Content-Type:[^\r\n]+\r?\n/g, '')
      .replace(/Content-Transfer-Encoding:[^\r\n]+\r?\n/g, '')
      .replace(/Content-Disposition:[^\r\n]+\r?\n/g, '')
      .replace(/Content-ID:[^\r\n]+\r?\n/g, '')
      .replace(/boundary=[^\r\n]+\r?\n/g, '')
      .trim();
  };
  
  // Fix double-encoded UTF-8 characters (happens when email clients mess up the encoding)
  const fixDoubleEncodedText = (text) => {
    if (!text) return text;
    
    // Common patterns in Nordic languages when UTF-8 is double-encoded
    return text
      // Handle cases where characters appear like "frÃ¥n" instead of "från"
      .replace(/Ã¥/g, 'å')
      .replace(/Ã¤/g, 'ä')
      .replace(/Ã¶/g, 'ö')
      .replace(/Ã©/g, 'é')
      .replace(/Ã¸/g, 'ø')
      .replace(/Ã¦/g, 'æ')
      .replace(/Ã…/g, 'Å')
      .replace(/Ã„/g, 'Ä')
      .replace(/Ã–/g, 'Ö')
      .replace(/Ã‰/g, 'É')
      .replace(/Ã˜/g, 'Ø')
      .replace(/Ã†/g, 'Æ');
  };

  // Check if this is HTML content
  if (/<[a-z][\s\S]*>/i.test(messageBody)) {
    // First, check if it's a multipart message with HTML
    if (messageBody.includes('Content-Type:') && 
        (messageBody.includes('boundary=') || messageBody.match(/--[0-9a-zA-Z]+/))) {
      
      // Try to extract HTML part
      const htmlPartMatch = messageBody.match(/Content-Type: text\/html;[\s\S]*?\r?\n\r?\n([\s\S]*?)(?=--|-\r?\n|$)/i);
      if (htmlPartMatch && htmlPartMatch[1]) {
        const decodedHtml = fixDoubleEncodedText(decodeQuotedPrintable(htmlPartMatch[1].trim()));
        return { 
          content: decodedHtml, 
          isHtml: true 
        };
      }
    }
    
    // Regular HTML content
    return { 
      content: messageBody, 
      isHtml: true 
    };
  }

  // Check if this is a multipart MIME message with boundaries
  if (messageBody.includes('Content-Type:') && 
      (messageBody.includes('boundary=') || messageBody.match(/--[0-9a-zA-Z]+/))) {
    
    console.log("Processing multipart MIME message");
    
    // Extract boundary pattern
    let boundary = null;
    const boundaryMatch = messageBody.match(/boundary="?([^";\r\n]+)"?/i) || 
                          messageBody.match(/--([0-9a-zA-Z]+(?:-[0-9a-zA-Z]+)*)/);
    
    if (boundaryMatch && boundaryMatch[1]) {
      boundary = boundaryMatch[1].trim();
      console.log("Detected boundary:", boundary);
    }
    
    // Try to extract the text part using multiple strategies
    try {
      // Strategy 1: Look for Content-Type: text/plain section and extract its content
      let plainTextContent = null;
      const plainTextMatch = messageBody.match(/Content-Type: text\/plain;[\s\S]*?\r?\n\r?\n([\s\S]*?)(?=--|-\r?\n|$)/i);
      
      if (plainTextMatch && plainTextMatch[1]) {
        plainTextContent = fixDoubleEncodedText(decodeQuotedPrintable(plainTextMatch[1].trim()));
        console.log("Found text/plain content:", plainTextContent.substring(0, 50) + "...");
        return { content: plainTextContent, isHtml: false };
      }
      
      // Strategy 2: If boundary is found, extract all text parts
      if (boundary) {
        // Find all boundary-separated parts
        const boundaryRegex = new RegExp(`--${boundary}[\\s\\S]*?\\r?\\n\\r?\\n([\\s\\S]*?)(?=--${boundary}|$)`, 'gi');
        let allParts = [];
        let match;
        
        while ((match = boundaryRegex.exec(messageBody)) !== null) {
          if (match[1]) {
            // Skip parts that are obviously not text
            if (!match[1].includes('Content-Type: image/') && 
                !match[1].includes('Content-Type: application/')) {
              // Clean and add this part
              const cleanedPart = removeMIMEHeadersAndBoundaries(match[1]);
              const decodedPart = fixDoubleEncodedText(decodeQuotedPrintable(cleanedPart));
              allParts.push(decodedPart);
            }
          }
        }
        
        if (allParts.length > 0) {
          const combinedText = allParts.join('\n\n').trim();
          console.log("Combined all text parts:", combinedText.substring(0, 50) + "...");
          return { content: combinedText, isHtml: false };
        }
      }
      
      // Strategy 3: Try to extract quoted-printable content
      if (messageBody.includes('Content-Transfer-Encoding: quoted-printable')) {
        // Find the message body after headers
        const match = messageBody.match(/\r?\n\r?\n([\s\S]*)/);
        if (match && match[1]) {
          const decoded = fixDoubleEncodedText(decodeQuotedPrintable(removeMIMEHeadersAndBoundaries(match[1])));
          console.log("Decoded quoted-printable content:", decoded.substring(0, 50) + "...");
          return { content: decoded, isHtml: false };
        }
      }
      
      // Strategy 4: Just look for any text after a blank line, filtering out MIME headers
      const lines = messageBody.split(/\r?\n/);
      let messageStart = -1;
      
      for (let i = 0; i < lines.length; i++) {
        // Skip lines that look like headers or boundaries
        if (lines[i].trim() === '' && 
            i+1 < lines.length && 
            !lines[i+1].includes('Content-') &&
            !lines[i+1].startsWith('--')) {
          messageStart = i + 1;
          break;
        }
      }
      
      if (messageStart >= 0) {
        // Get text from message start to end or next boundary
        const messageLines = [];
        for (let i = messageStart; i < lines.length; i++) {
          // Stop at boundary marker
          if (lines[i].startsWith('--') && (boundary && lines[i].includes(boundary))) {
            break;
          }
          // Skip additional headers that might be in the content
          if (!lines[i].includes('Content-Type:') && 
              !lines[i].includes('Content-Transfer-Encoding:')) {
            messageLines.push(lines[i]);
          }
        }
        
        if (messageLines.length > 0) {
          const extractedText = messageLines.join('\n');
          const cleanedText = fixDoubleEncodedText(decodeQuotedPrintable(extractedText));
          console.log("Found content using line-by-line strategy:", cleanedText.substring(0, 50) + "...");
          return { content: cleanedText, isHtml: false };
        }
      }
      
      // Strategy 5: Most aggressive cleanup - remove all MIME headers and boundaries
      const fullyCleanedMessage = removeMIMEHeadersAndBoundaries(messageBody);
      const decodedMessage = fixDoubleEncodedText(decodeQuotedPrintable(fullyCleanedMessage));
      
      if (decodedMessage && decodedMessage.trim().length > 0) {
        console.log("Using fully cleaned message:", decodedMessage.substring(0, 50) + "...");
        return { content: decodedMessage, isHtml: false };
      }
      
      // If all else fails
      console.log("All extraction strategies failed");
      return { 
        content: "Could not parse email content correctly.", 
        isHtml: false 
      };
    } catch (error) {
      console.error("Error parsing email content:", error);
      return { 
        content: "Error parsing email content: " + error.message, 
        isHtml: false 
      };
    }
  }

  // Otherwise treat as plain text
  return { 
    content: fixDoubleEncodedText(messageBody), 
    isHtml: false 
  };
};